import React, { useState } from 'react'

import Autocomplete, {
	type AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete'
import { useIntl } from 'react-intl'
import { InfiniteData } from '@tanstack/react-query'
import type { UserPaginated } from '@matech/thebigpos-sdk'
import CircularProgress from '@mui/material/CircularProgress'

import { LoanUserRow } from '@/components/loanAppDetails/loan-users-table/types'
import {
	concatStrings,
	addSpacesToPascalCase,
} from '../services/helpers'
import { TextField } from './formControls'
import { Roles } from '../services/client'
import useDebounce from '../hooks/useDebounce'
import usePaginatedUsers from '../hooks/usePaginatedUsers'
import { DEFAULT_PAGE_SIZE } from '../constants/pagination'

const USERS_DEFAULT_SORT_BY = 'firstName'
const USERS_DEFAULT_ORDER_BY = 'asc'
const USERS_DEFAULT_QUERY_STALE_TIME = 60 * 1_000 * 5
const USERS_DEFAULT_FILTER_ROLES_USERS = [
	Roles.admin,
	Roles.realtor,
	Roles.loanOfficer,
	Roles.loanProcessor,
	Roles.settlementAgent,
]

type UsersSelectProps = {
	value?: LoanUserRow | null
	filterRoles?: string[]
	onOptionChange?: (option: LoanUserRow | null) => void
	onInputChange?: (
		value: string,
		reason: AutocompleteInputChangeReason
	) => void
	onOptionClose?: () => void
	disabled?: boolean
	error?: boolean
	helperText?: string
	textFieldRef?: React.Ref<HTMLDivElement>
}

const parsePaginatedUserListToFlat = (
	data: InfiniteData<UserPaginated, number>
) => {
	return (
		data?.pages.flatMap((userListPage) =>
			userListPage.rows.map(
				(rowItem) =>
					({
						id: rowItem.id,
						name: concatStrings(
							rowItem.firstName,
							rowItem.lastName
						).getValue(),
						email: rowItem.email,
						role: rowItem.role,
						createdAt: rowItem.createdAt,
					}) as LoanUserRow
			)
		) || []
	)
}

// ToDo: This component will use an abstract Autocomplete from Material in the future
const UsersSelect = ({
	value,
	error,
	disabled,
	filterRoles,
	onInputChange,
	onOptionClose,
	onOptionChange,
	textFieldRef,
	helperText,
}: UsersSelectProps) => {
	const [searchText, setSearchText] = useState('')
	const [localInputValue, setLocalInputValue] = useState('')

	const { formatMessage } = useIntl()
	const debounce = useDebounce<string>()

	const {
		data: userList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isLoadingUserList,
	} = usePaginatedUsers({
		params: {
			searchText,
			roles: filterRoles ?? USERS_DEFAULT_FILTER_ROLES_USERS,
		},
		paginationParams: {
			pageSize: DEFAULT_PAGE_SIZE,
			sortBy: USERS_DEFAULT_SORT_BY,
			sortDirection: USERS_DEFAULT_ORDER_BY,
		},
		staleTime: USERS_DEFAULT_QUERY_STALE_TIME,
		select: parsePaginatedUserListToFlat,
	})

	const handleChangeUser = (
		e: React.SyntheticEvent<Element, Event>,
		value: LoanUserRow | null
	) => onOptionChange?.(value)

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		onInputChange?.(value, reason)

		debounce(value, (debouncedValue) =>
			setSearchText(debouncedValue.toLowerCase())
		)

		setLocalInputValue(value)
	}

	/*
        ToDo: Remove these inline functions after create a more complete Autocomplete component
        Task Name: [WEB] - Create Autocomplete component
        Task Id: 9696024993
        Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/9696024993
    */
	return (
		<Autocomplete<LoanUserRow>
			options={userList || []}
			getOptionLabel={(option) => option.name}
			value={value}
			onChange={handleChangeUser}
			inputValue={localInputValue}
			onInputChange={handleInputChange}
			onClose={onOptionClose}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			loading={isLoadingUserList}
			disabled={disabled}
			renderInput={(params) => (
				<TextField
					{...params}
					disabled={disabled}
					inputRef={textFieldRef}
					label={formatMessage({
						id: 'share_loan.select_user',
					})}
					error={error}
					helperText={helperText}
					fullWidth
					required
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoadingUserList || isFetchingNextPage ? (
									<CircularProgress color="inherit" size={20} />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			renderOption={(props, option) => (
				<li
					{...props}
					key={option.id}
					onMouseEnter={() => {
						if (!userList) return

						const optionIndex = userList?.findIndex(
							(u) => u.id === option.id
						)

						if (
							optionIndex >= userList.length - 5 &&
							hasNextPage &&
							!isFetchingNextPage &&
							userList.length >= 10
						) {
							fetchNextPage()
						}
					}}
				>
					<div>
						<div className="font-medium">{option.name}</div>
						<div className="text-sm text-gray-500">
							{addSpacesToPascalCase(option.role)}
						</div>
					</div>
				</li>
			)}
			ListboxProps={{
				onScroll: (event) => {
					if (!userList) return

					const listboxNode = event.currentTarget
					const isNearBottom =
						listboxNode.scrollTop + listboxNode.clientHeight >=
						listboxNode.scrollHeight / 2

					if (
						isNearBottom &&
						hasNextPage &&
						!isFetchingNextPage &&
						userList.length >= DEFAULT_PAGE_SIZE
					) {
						fetchNextPage()
					}
				},
			}}
			noOptionsText={
				!localInputValue
					? formatMessage({
							id: 'share_loan.start_typing_to_search',
						})
					: formatMessage({
							id: 'share_loan.no_users_found',
						})
			}
		/>
	)
}

export default UsersSelect
